import React, { useEffect, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { Collapse, Form, Row, Col, Label, Input } from "reactstrap";
import { getPropertyFilters } from "./TenantForm/Helpers/api";

// Import necessary styles for DatePicker if not already imported in your project
import "react-datepicker/dist/react-datepicker.css";

const FilterSection = ({ isFilterOpen, filterData, handleFilterChange }) => {
    const [dynamicFilters, setDynamicFilters] = useState([]);
    const [loading, setLoading] = useState(true);

    const leaseExpirationOptions = [
        { value: "7", label: "7 days" },
        { value: "15", label: "15 days" },
        { value: "30", label: "30 days" },
        { value: "45", label: "45 days" },
        { value: "60", label: "60 days" },
        { value: "90", label: "90 days" },
    ];

    // Helper function to format date as YYYY-MM-DD without timezone conversion issues
    const formatDateToMST = (date) => {
        if (!date) return "";

        // Use the local date values directly to avoid timezone conversion issues
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    // Helper function to parse date string back to Date object
    const parseDate = (dateString) => {
        if (!dateString) return null;

        // Parse YYYY-MM-DD format directly to avoid timezone issues
        const [year, month, day] = dateString.split('-').map(Number);
        if (!year || !month || !day) return null;

        // Create date using local timezone to avoid shifts
        const date = new Date(year, month - 1, day);
        return isNaN(date.getTime()) ? null : date;
    };

    useEffect(() => {
        const fetchFilters = async () => {
            setLoading(true);
            try {
                const response = await getPropertyFilters({
                    property_id: filterData.property_id,
                    building_name: filterData.building_name
                });

                if (response.data && response.success) {
                    setDynamicFilters(response.data);
                }
            } catch (error) {
                console.error("Error fetching filters:", error);
            } finally {
                setLoading(false);
            }
        };

        if (filterData.property_id) {
            fetchFilters();
        }
    }, [filterData.property_id, filterData.building_name]);

    const calculateColumnWidth = () => {
        const totalFilters = dynamicFilters.length + 1; // +1 for lease expiration filter

        if (totalFilters <= 2) return 6;  // Two filters: half width each
        if (totalFilters <= 3) return 4;  // Three filters: third width each
        if (totalFilters <= 4) return 3;  // Four filters: quarter width each
        if (totalFilters <= 6) return 2;  // Up to six filters: 1/6th width each
        return 4;  // More than 6 filters: arrange in rows of 3
    };

    const needsExtraWidth = (fieldName) => {
        const lowerName = fieldName.toLowerCase();
        return lowerName.includes('description') ||
            lowerName.includes('comment') ||
            lowerName.includes('address');
    };

    const handleFilterChangeWrapper = (event) => {
        handleFilterChange(event);
    };

    const handleLeaseExpirationChange = (selected) => {
        handleFilterChangeWrapper({
            target: { name: "lease_expiration_days", value: selected?.value || "" }
        });
    };

    const renderLeaseExpirationFilter = () => {
        const colWidth = calculateColumnWidth();

        return (
            <Col md={colWidth} key="lease-expiration">
                <Label>Lease Expiring Within</Label>
                <Select
                    name="lease_expiration_select"
                    options={leaseExpirationOptions}
                    value={leaseExpirationOptions.find(opt => opt.value === filterData.lease_expiration_days) || null}
                    onChange={handleLeaseExpirationChange}
                    placeholder="Select days"
                    isClearable
                    classNamePrefix="react-select"
                    styles={{
                        menu: (provided) => ({
                            ...provided,
                            zIndex: 9999,
                        }),
                    }}
                />
            </Col>
        );
    };

    const renderFilterField = (filter) => {
        const fieldName = filter.field;
        const displayName = filter.displayName;
        const filterType = filter.filterType || 'text';

        let colWidth = calculateColumnWidth();
        if (needsExtraWidth(fieldName)) {
            colWidth = Math.min(colWidth * 2, 12);
        }
        const currentValue = filterData[fieldName];

        switch (filterType) {
            case 'date':
                return (
                    <Col md={colWidth} key={fieldName}>
                        <Label>{displayName}</Label>
                        <br />
                        <DatePicker
                            selected={parseDate(currentValue)}
                            onChange={(date) => {
                                // Use the simpler formatDateToMST function to avoid date shifts
                                const dateString = formatDateToMST(date);
                                handleFilterChangeWrapper({
                                    target: { name: fieldName, value: dateString }
                                });
                            }}
                            className="form-control"
                            placeholderText={`Select ${displayName}`}
                            isClearable
                            dateFormat="yyyy-MM-dd"
                            showTimeSelect={false}
                        />
                    </Col>
                );

            case 'number':
                return (
                    <Col md={colWidth} key={fieldName}>
                        <Label>{displayName}</Label>
                        <Input
                            type="number"
                            name={fieldName}
                            value={currentValue !== undefined && currentValue !== null ? String(currentValue) : ""}
                            onChange={handleFilterChangeWrapper}
                            placeholder={`Enter ${displayName}`}
                        />
                    </Col>
                );

            case 'boolean':
                return (
                    <Col md={colWidth} key={fieldName}>
                        <Label>{displayName}</Label>
                        <Select
                            name={fieldName}
                            options={[
                                { value: "true", label: "Yes" },
                                { value: "false", label: "No" }
                            ]}
                            value={
                                currentValue !== undefined && currentValue !== null && currentValue !== "" ?
                                    {
                                        value: String(currentValue),
                                        label: String(currentValue) === "true" ? "Yes" : "No"
                                    } :
                                    null
                            }
                            onChange={(selected) => handleFilterChangeWrapper({
                                target: { name: fieldName, value: selected?.value || "" }
                            })}
                            isClearable
                            placeholder={`Select ${displayName}`}
                        />
                    </Col>
                );

            case 'multiselect':
                const multiSelectValue = Array.isArray(currentValue) ? currentValue :
                    (currentValue ? [currentValue] : []);

                return (
                    <Col md={colWidth} key={fieldName}>
                        <Label>{displayName}</Label>
                        <Select
                            name={fieldName}
                            options={filter.options || []}
                            value={
                                multiSelectValue.length > 0 ?
                                    multiSelectValue.map(val =>
                                        filter.options?.find(opt => opt.value === val) || { value: val, label: val }
                                    ) :
                                    null
                            }
                            onChange={(selected) => handleFilterChangeWrapper({
                                target: {
                                    name: fieldName,
                                    value: selected ? selected.map(item => item.value) : []
                                }
                            })}
                            isMulti
                            placeholder={`Select ${displayName}`}
                            classNamePrefix="react-select"
                        />
                    </Col>
                );

            case 'select':
                return (
                    <Col md={colWidth} key={fieldName}>
                        <Label>{displayName}</Label>
                        <Select
                            name={fieldName}
                            options={filter.options || []}
                            value={filter.options?.find(opt => opt.value === currentValue) || null}
                            onChange={(selected) => handleFilterChangeWrapper({
                                target: { name: fieldName, value: selected?.value || "" }
                            })}
                            placeholder={`Select ${displayName}`}
                            isClearable
                            classNamePrefix="react-select"
                        />
                    </Col>
                );

            case 'text':
            default:
                return (
                    <Col md={colWidth} key={fieldName}>
                        <Label>{displayName}</Label>
                        <Input
                            type="text"
                            name={fieldName}
                            value={currentValue !== undefined && currentValue !== null ? String(currentValue) : ""}
                            onChange={handleFilterChangeWrapper}
                            placeholder={`Enter ${displayName}`}
                        />
                    </Col>
                );
        }
    };

    return (
        <Collapse isOpen={isFilterOpen}>
                    {loading ? (
                        <div className="text-center">Loading filters...</div>
                    ) : (
                        <Form>
                            <Row className="g-3">
                                {dynamicFilters.length > 0 ? (
                                    dynamicFilters.map(filter => renderFilterField(filter))
                                ) : (
                                    <Col md={12}>
                                        <div className="text-center text-muted">
                                            No additional filters available for this property
                                        </div>
                                    </Col>
                                )}
                                {renderLeaseExpirationFilter()}
                            </Row>
                        </Form>
                    )}
        </Collapse>
    );
};

export { FilterSection };